import { z } from 'zod'

const contactRefSchema = z.string().regex(/^contact_v1_[0-9a-f]{64}$/)
const aggregateIdSchema = z.string().regex(/^ia_v1_[0-9a-f]{64}$/)

export const contactSearchResultSchema = z
  .object({
    schemaVersion: z.literal(1),
    contactRef: contactRefSchema,
    displayName: z.string().trim().min(1).max(120),
    phoneDisplay: z.string().max(40).nullable(),
    emailDisplay: z.string().max(254).nullable(),
    relationship: z.literal('FLIO_CONTACT'),
    providerLinkStatus: z.enum(['NONE', 'RECORDED', 'CONFLICTED']),
    destinationState: z.enum([
      'DESTINATION_CONFIRMED',
      'DESTINATION_UNAVAILABLE',
      'DESTINATION_AMBIGUOUS',
    ]),
    consentEvidenceState: z.enum([
      'OPTED_OUT',
      'EXISTING_INBOUND_SMS_RELATIONSHIP',
      'NO_POSITIVE_CONSENT_EVIDENCE',
      'ELIGIBILITY_REVIEW_REQUIRED',
    ]),
    identityState: z.enum([
      'EXACT_CONTACT_AUTHORITY',
      'RECONCILIATION_REQUIRED',
      'CONFLICTED',
    ]),
    existingInteractionAggregateId: aggregateIdSchema.nullable(),
    selectionAvailable: z.boolean(),
  })
  .strict()

export const contactSearchResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    results: z.array(contactSearchResultSchema).max(50),
    nextCursor: z.string().min(1).max(768).nullable(),
  })
  .strict()

const legacyContactSelectionResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    contactSelectionRef: z
      .string()
      .regex(/^contact_selection_v1_[0-9a-f]{64}$/),
    contactRef: contactRefSchema,
    expiresAtMs: z.number().int().positive(),
    evidence: z
      .object({
        identityState: z.literal('EXACT_CONTACT_AUTHORITY'),
        destinationState: z.literal('DESTINATION_CONFIRMED'),
        consentEvidenceState: z.enum([
          'EXISTING_INBOUND_SMS_RELATIONSHIP',
          'NO_POSITIVE_CONSENT_EVIDENCE',
        ]),
      })
      .strict(),
    existingInteractionAggregateId: aggregateIdSchema.nullable(),
  })
  .strict()

const staffPatientContactSelectionResponseSchema = z
  .object({
    schemaVersion: z.literal(2),
    contactSelectionRef: z
      .string()
      .regex(/^contact_selection_v1_[0-9a-f]{64}$/),
    contactRef: contactRefSchema,
    expiresAtMs: z.number().int().positive(),
    evidence: z
      .object({
        identityState: z.literal('EXACT_CONTACT_AUTHORITY'),
        destinationState: z.literal('DESTINATION_CONFIRMED'),
        consentEvidenceState: z.literal(
          'EXPLICIT_STAFF_PATIENT_FIRST_CONTACT'
        ),
      })
      .strict(),
    existingInteractionAggregateId: aggregateIdSchema.nullable(),
  })
  .strict()

export const contactSelectionResponseSchema = z.discriminatedUnion(
  'schemaVersion',
  [
    legacyContactSelectionResponseSchema,
    staffPatientContactSelectionResponseSchema,
  ]
)

export const startConversationResponseSchema = z
  .object({
    schemaVersion: z.literal(1),
    startCommandRef: z.string().regex(/^start_conversation_v1_[0-9a-f]{64}$/),
    status: z.enum([
      'IN_PROGRESS',
      'ACCEPTED',
      'RECONCILIATION_REQUIRED',
      'FAILED_SAFE',
    ]),
    aggregateId: aggregateIdSchema,
    ownershipState: z.enum(['AI_ACTIVE', 'HUMAN_TAKEOVER']),
    ownershipRevision: z.number().int().nonnegative(),
    dispatchState: z.enum([
      'NOT_STARTED',
      'ACCEPTED',
      'DISPATCHING',
      'SENT',
      'FAILED_TERMINAL',
      'RECONCILIATION_REQUIRED',
    ]),
    replayClassification: z.enum([
      'APPLIED',
      'EXACT_REPLAY',
      'RESUMED',
      'STATUS_READ',
      'RECOVERED',
    ]),
  })
  .strict()

export type ContactSearchResult = z.infer<typeof contactSearchResultSchema>
export type ContactSearchResponse = z.infer<typeof contactSearchResponseSchema>
export type ContactSelection = z.infer<typeof contactSelectionResponseSchema>
export type StartConversationResult = z.infer<
  typeof startConversationResponseSchema
>
